import { useEffect, useRef, useState } from "react";
import "./OverflowScrollCue.css";

const TRACK_INSET = 6;
const MIN_THUMB_HEIGHT = 28;

export const getScrollCueMetrics = (target, cueWidth = 4) => {
  if (!target || target.scrollHeight <= target.clientHeight + 1) return null;

  const trackHeight = Math.max(0, target.clientHeight - TRACK_INSET * 2);
  const thumbHeight = Math.min(
    trackHeight,
    Math.max(
      MIN_THUMB_HEIGHT,
      trackHeight * (target.clientHeight / target.scrollHeight),
    ),
  );
  const availableScroll = target.scrollHeight - target.clientHeight;
  const availableThumbTravel = Math.max(0, trackHeight - thumbHeight);
  const thumbTop = availableScroll
    ? availableThumbTravel * (target.scrollTop / availableScroll)
    : 0;

  return {
    top: target.offsetTop + TRACK_INSET,
    left: target.offsetLeft + target.clientWidth - cueWidth,
    trackHeight,
    thumbHeight,
    thumbTop,
  };
};

const OverflowScrollCue = ({
  targetRef,
  watchKey,
  revealOnHover = false,
  cueWidth = 4,
}) => {
  const [cue, setCue] = useState(null);
  const hideTimerRef = useRef(null);
  const wasScrollableRef = useRef(false);
  const isVisibleRef = useRef(false);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return undefined;

    let animationFrame = null;

    const clearHideTimer = () => {
      if (!hideTimerRef.current) return;
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    };

    const refresh = ({ reveal = false, persist = false } = {}) => {
      const metrics = getScrollCueMetrics(target, cueWidth);
      const isScrollable = Boolean(metrics);
      const becameScrollable = isScrollable && !wasScrollableRef.current;
      wasScrollableRef.current = isScrollable;

      if (!metrics) {
        clearHideTimer();
        isVisibleRef.current = false;
        setCue(null);
        return;
      }

      const shouldReveal = reveal || becameScrollable;
      if (shouldReveal) {
        clearHideTimer();
        isVisibleRef.current = true;
        if (!persist) {
          hideTimerRef.current = setTimeout(() => {
            isVisibleRef.current = false;
            setCue((current) =>
              current ? { ...current, visible: false } : current,
            );
          }, reveal ? 850 : 1800);
        }
      }

      setCue({ ...metrics, visible: shouldReveal || isVisibleRef.current });
    };

    const scheduleRefresh = (options) => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        refresh(options);
      });
    };

    scheduleRefresh();

    const handleScroll = () =>
      scheduleRefresh({ reveal: true, persist: isHoveredRef.current });
    const handleMouseEnter = () => {
      isHoveredRef.current = true;
      scheduleRefresh({ reveal: true, persist: true });
    };
    const handleMouseLeave = () => {
      isHoveredRef.current = false;
      clearHideTimer();
      isVisibleRef.current = false;
      setCue((current) =>
        current ? { ...current, visible: false } : current,
      );
    };
    const handleTransitionEnd = () => scheduleRefresh();
    const handleWindowResize = () => scheduleRefresh();
    target.addEventListener("scroll", handleScroll, { passive: true });
    target.addEventListener("transitionend", handleTransitionEnd);
    if (revealOnHover) {
      target.addEventListener("mouseenter", handleMouseEnter);
      target.addEventListener("mouseleave", handleMouseLeave);
    }
    window.addEventListener("resize", handleWindowResize);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => scheduleRefresh());
    resizeObserver?.observe(target);
    Array.from(target.children).forEach((child) => resizeObserver?.observe(child));

    const mutationObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(() => scheduleRefresh());
    mutationObserver?.observe(target, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      clearHideTimer();
      if (animationFrame) cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      target.removeEventListener("scroll", handleScroll);
      target.removeEventListener("transitionend", handleTransitionEnd);
      target.removeEventListener("mouseenter", handleMouseEnter);
      target.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", handleWindowResize);
      wasScrollableRef.current = false;
      isVisibleRef.current = false;
      isHoveredRef.current = false;
    };
  }, [cueWidth, revealOnHover, targetRef, watchKey]);

  if (!cue) return null;

  return (
    <span
      className={`overflow-scroll-cue${cue.visible ? " overflow-scroll-cue--visible" : ""}`}
      style={{
        top: `${cue.top}px`,
        left: `${cue.left}px`,
        height: `${cue.trackHeight}px`,
        width: `${cueWidth}px`,
      }}
      aria-hidden="true"
    >
      <span
        className="overflow-scroll-cue__thumb"
        style={{
          height: `${cue.thumbHeight}px`,
          transform: `translateY(${cue.thumbTop}px)`,
        }}
      />
    </span>
  );
};

export default OverflowScrollCue;
